import React from "react";
import PrintDialog from "./PrintDialog";
import { useLanguage } from "../../Language";
import { useAccount } from "../../Account";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";

import dayjs from "dayjs";
import { useIsMobile } from "@tlc/ui/useIsMobile";

export const getDateString = (date: string | Date | number): string => {
  return new Date(date).toLocaleDateString();
};

export interface AccountPrintLoansDialogProps {
  open: boolean;
  onClose: () => void;
  loans: HostSystemItemView[];
  isLoading?: boolean;
}

export default function AccountPrintLoansDialog({
  open,
  onClose,
  loans,
  isLoading = false,
}: AccountPrintLoansDialogProps) {
  const { translate } = useLanguage();
  const { accountSummary } = useAccount();
  const isMobile = useIsMobile();

  const getRenewalsInfoToPrint = (loan: HostSystemItemView): string => {
    if (loan.downloadable) {
      return "";
    }

    const renewalCount = loan.renewalCount;

    if (loan.renewalLimit === null) {
      return `${renewalCount} / ${translate("renewalsUnlimited")}`;
    }

    const renewalsLeft = Math.max(0, loan.renewalLimit - renewalCount);
    return `${renewalCount} / ${renewalsLeft}`;
  };

  const getDaysOverdue = (dueDate: Date | string): number => {
    return dayjs().startOf("day").diff(dayjs(dueDate).endOf("day"), "day");
  };

  const getDaysRemainingForLoans = (loan: HostSystemItemView): string => {
    if (!loan.dueDate) {
      return "";
    }

    const date = new Date(loan.dueDate);
    const days = getDaysOverdue(date);

    if (days === 0) {
      return `${translate("dueToday")}`;
    } else if (days > 0) {
      return `${translate("overdue")}`;
    } else {
      return `${Math.abs(days)} ${translate("daysRemaining")}`;
    }
  };

  const buildHtml = () => {
    if (!loans || loans.length === 0) return "";

    const isSerial = loans.some(
      (loan) =>
        (loan.enumeration && loan.enumeration.length > 0) ||
        (loan.chronology && loan.chronology.length > 0),
    );

    let html = `
    <div>
      <h2 align="center">${translate("exportData_currentLoans")}</h2>
      <table width="100%" cellpadding="5">
        <tbody>
          ${loans
            .map(
              (loan, index) => `
            <tr>
              <td valign="top" style="padding: 12px 0; border-bottom: 1px solid #eee;">
                <p style="margin: 0 0 8px; font-weight: bold; font-size: 16px">
                  ${index + 1}. ${loan.title ?? ""}${
                loan.author ? ` : ${loan.author}` : ""
              }
                </p>
                <div style="font-size: 14px; display: flex; flex-direction: column; gap: 4px;">
                  <div>
                    <span style="color: #000000;">${translate(
                      "exportData_dueDate",
                    )}: </span>
                    <span>${loan.dueDateString ?? ""}</span>
                  </div>
                  <div>
                    <span style="color: #000000;">${translate(
                      "exportData_status",
                    )}: </span>
                    <span>${getDaysRemainingForLoans(loan)}</span>
                  </div>
                  ${(() => {
                    const renewalsInfo = getRenewalsInfoToPrint(loan);
                    return renewalsInfo
                      ? `
                        <div>
                          <span style="color: #000000;">${translate(
                            "exportData_renewedAndRemaining",
                          )}: </span>
                          <span>${renewalsInfo}</span>
                        </div>
                      `
                      : "";
                  })()}
                  ${
                    isSerial
                      ? `
                  <div>
                    <span style="color: #000000;">${translate(
                      "exportData_chronology",
                    )}: </span>
                    <span>${loan.chronology ?? ""}</span>
                  </div>
                  <div>
                    <span style="color: #000000;">${translate(
                      "exportData_numbering",
                    )}: </span>
                    <span>${loan.enumeration ?? ""}</span>
                  </div>
                  `
                      : ""
                  }
                </div>
              </td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
    <br class="pagebreak">
  `;

    return html;
  };

  return (
    <PrintDialog
      open={open}
      onClose={onClose}
      dialogTitle={translate("exportData_currentLoans")}
      htmlContent={buildHtml()}
      isLoading={isLoading}
      disabled={accountSummary.loanCount === 0}
      maxWidth={"md"}
      fullScreen={isMobile}
    />
  );
}
